<script setup lang="ts">
import CurrencyText from '@uiKit/components/CurrencyText.vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import InfoSheetButton from '@uiKit/components/InfoSheetButton.vue'
import { TokenDiamondIcon } from '@uiKit/icons'
import CurrencySymbol from '@uiKit/components/CurrencySymbol.vue'
import { formatTokenPrice } from '@shared/utils'
import { formatWhole, type PropertyFinancialsView } from '../utils/propertyFinancialUtils'

// PROPERTY FINANCIALS — top of the right rail (Figma 32590:62196): Sliceal
// price vs market price tiles, then price per token and the sold / available
// token split.

interface Props {
  financials: PropertyFinancialsView
}

defineProps<Props>()

// "AED 0.92" → "0.92": the dirham glyph stands in for the currency code.
const tokenPriceDigits = (price: number) => formatTokenPrice(price).replace(/^AED\s*/, '')
</script>

<template>
  <Box is="section" direction="col" gap="14px" class="pd-financials">
    <Text type="body" weight="semibold" color="muted" transform="uppercase" is="h2">
      Property Financials
    </Text>

    <Box direction="col" gap="3">
      <Box gap="3" class="pd-financials__tiles">
        <Box direction="col" justify="center" gap="6px" class="pd-financials__tile">
          <Box align="center" justify="between" gap="2">
            <Text
              type="body"
              weight="bold"
              color="var(--p-surface-400)"
              transform="uppercase"
              is="span"
            >
              Sliceal Price
            </Text>
            <InfoSheetButton
              v-if="financials.priceTile.sheet"
              :content="financials.priceTile.sheet"
              :label="financials.priceTile.sheet.title"
              :size="14"
            />
          </Box>
          <Text type="heading" weight="bold" is="span"
            ><CurrencyText :text="financials.priceTile.value ?? '-'"
          /></Text>
          <Box v-if="financials.priceTile.caption" align="center" gap="2px">
            <Box align="center" justify="center" class="pd-financials__discount-box">
              <PiIcon name="percentage" class="pd-financials__discount-icon" />
            </Box>
            <Text type="body" weight="medium" color="var(--p-success-600)" is="span">
              <CurrencyText :text="financials.priceTile.caption" />
            </Text>
          </Box>
        </Box>

        <Box direction="col" justify="center" gap="6px" class="pd-financials__tile">
          <Box align="center" justify="between" gap="2">
            <Text
              type="body"
              weight="bold"
              color="var(--p-surface-400)"
              transform="uppercase"
              is="span"
            >
              Market Price
            </Text>
            <InfoSheetButton
              v-if="financials.marketTile.sheet"
              :content="financials.marketTile.sheet"
              :label="financials.marketTile.sheet.title"
              :size="14"
            />
          </Box>
          <Text type="heading" weight="bold" is="span"
            ><CurrencyText :text="financials.marketTile.value ?? '-'"
          /></Text>
          <Text type="body" weight="semibold" color="var(--p-surface-400)" is="span">
            <CurrencyText :text="financials.marketTile.caption" />
          </Text>
        </Box>
      </Box>

      <Box direction="col" gap="14px" class="pd-financials__tokens">
        <Box direction="col" gap="2">
          <Box align="center" justify="between">
            <Text
              type="caption"
              weight="semibold"
              color="muted"
              transform="uppercase"
              is="span"
              class="pd-financials__eyebrow"
            >
              Price per token
            </Text>
            <InfoSheetButton
              :content="financials.tokenPriceSheet"
              :label="financials.tokenPriceSheet.title"
            />
          </Box>
          <Text type="page-title" weight="bold" is="span"
            ><CurrencySymbol />{{ tokenPriceDigits(financials.tokenPrice) }}</Text
          >
        </Box>

        <Box direction="col" gap="2">
          <Text
            type="caption"
            weight="semibold"
            color="muted"
            transform="uppercase"
            is="span"
            class="pd-financials__eyebrow"
          >
            Token breakdown
          </Text>
          <Box direction="col" gap="4">
            <Box
              class="pd-financials__bar"
              role="img"
              :aria-label="`${Math.round(financials.tokens.soldPct)}% of tokens sold`"
            >
              <Box
                class="pd-financials__bar-fill"
                :style="{ width: `${financials.tokens.soldPct}%` }"
              />
            </Box>

            <Box direction="col" gap="10px">
              <Box direction="col" gap="2" class="pd-financials__split">
                <Box align="center" gap="2">
                  <Box class="pd-financials__dot pd-financials__dot--sold" />
                  <Text
                    type="body"
                    color="var(--p-surface-600)"
                    is="span"
                    class="pd-financials__row-label"
                  >
                    Sold Tokens
                  </Text>
                  <Box align="center" gap="1">
                    <TokenDiamondIcon :size="20" />
                    <Text type="body" color="var(--p-surface-600)" align="right" is="span">{{
                      formatWhole(financials.tokens.sold)
                    }}</Text>
                  </Box>
                </Box>
                <Box align="center" gap="2">
                  <Box class="pd-financials__dot pd-financials__dot--available" />
                  <Text
                    type="body"
                    color="var(--p-surface-600)"
                    is="span"
                    class="pd-financials__row-label"
                  >
                    Available Tokens
                  </Text>
                  <Box align="center" gap="5px">
                    <TokenDiamondIcon :size="20" />
                    <Text type="body" color="var(--p-surface-600)" align="right" is="span">
                      {{ formatWhole(financials.tokens.available) }}
                    </Text>
                  </Box>
                </Box>
              </Box>
              <Box align="center" gap="2">
                <Text type="body" weight="medium" is="span" class="pd-financials__row-label"
                  >Total Tokens</Text
                >
                <Box align="center" gap="5px">
                  <TokenDiamondIcon :size="20" />
                  <Text
                    type="body"
                    weight="semibold"
                    align="right"
                    is="span"
                    class="pd-financials__total-value"
                    >{{ formatWhole(financials.tokens.total) }}</Text
                  >
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
.pd-financials__tile {
  flex: 1 1 0;
  min-width: 0;
  padding: 12px;
  border: 1px solid var(--p-primary-100);
  border-radius: 16px;
  background: var(--p-surface-0);
}

.pd-financials__discount-box {
  flex-shrink: 0;
  width: 14px;
  height: 14px;
}

.pd-financials__discount-icon {
  font-size: 12px;
  color: var(--p-success-600);
}

.pd-financials__total-value {
  min-width: 66px;
}

.pd-financials__tokens {
  padding: 16px;
  border: 1px solid var(--p-primary-100);
  border-radius: 14px;
  background: var(--p-surface-0);
}

.pd-financials__eyebrow {
  letter-spacing: 0.6px;
}

/* Unsold share in brand violet; the sold share overlays it in mint. */
.pd-financials__bar {
  position: relative;
  height: 11px;
  border-radius: 8px;
  overflow: hidden;
  background: var(--p-primary-500);
}

.pd-financials__bar-fill {
  height: 100%;
  border-radius: 8px;
  background: var(--p-emerald-400);
  transition: width 0.4s ease;
}

.pd-financials__split {
  padding-bottom: 8px;
  border-bottom: 1px solid var(--p-surface-200);
}

.pd-financials__row-label {
  flex: 1;
  min-width: 0;
}

.pd-financials__dot {
  flex-shrink: 0;
  width: 7px;
  height: 7px;
  border-radius: 50%;
}

.pd-financials__dot--sold {
  background: var(--p-emerald-400);
}

.pd-financials__dot--available {
  background: var(--p-primary-500);
}
</style>
